import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellRing, CheckCircle, AlertTriangle, Info, Check, CheckCircle2 } from 'lucide-react';
import { useNotification } from '../context/NotificationContext';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

export default function NotificationsPage() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotification();
  const [filter, setFilter] = useState('all'); // 'all' or 'unread'
  const navigate = useNavigate();

  const filteredNotifications = notifications.filter(n => filter === 'all' ? true : !n.isRead);

  const getIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle2 className="w-6 h-6 text-emerald-500" />;
      case 'warning': return <AlertTriangle className="w-6 h-6 text-amber-500" />;
      default: return <Info className="w-6 h-6 text-blue-500" />;
    }
  };

  const handleNotificationClick = (notif) => {
    if (!notif.isRead) markAsRead(notif.id);
    if (notif.link) {
      navigate(notif.link);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700 max-w-4xl mx-auto" dir="rtl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 p-6 md:p-8 rounded-3xl shadow-sm">
        <div className="flex items-center gap-5">
          <div className="w-14 h-14 bg-primary-50 dark:bg-primary-500/10 rounded-2xl flex items-center justify-center border border-primary-100 dark:border-primary-500/20 shrink-0">
            <BellRing className="w-7 h-7 text-primary-600 dark:text-primary-500" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white">الإشعارات</h1>
            <p className="text-slate-500 dark:text-zinc-400 font-medium mt-1">
              تابع آخر التحديثات والأنشطة الخاصة بك
            </p>
          </div>
        </div>
        
        {unreadCount > 0 && (
          <button 
            onClick={markAllAsRead}
            className="flex items-center gap-2 px-5 py-3 bg-slate-100 dark:bg-zinc-800 hover:bg-slate-200 dark:hover:bg-zinc-700 text-slate-700 dark:text-zinc-300 rounded-xl font-bold transition-colors"
          >
            <Check className="w-5 h-5" />
            تحديد الكل كمقروء
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-3 p-1.5 bg-slate-200/50 dark:bg-zinc-900/50 backdrop-blur-sm border border-slate-200 dark:border-white/5 rounded-2xl w-fit">
        <button
          onClick={() => setFilter('all')}
          className={`px-6 py-2.5 rounded-xl font-bold transition-all ${
            filter === 'all' 
              ? 'bg-white dark:bg-zinc-800 text-slate-900 dark:text-white shadow-sm' 
              : 'text-slate-500 dark:text-zinc-400 hover:text-slate-700 dark:hover:text-zinc-200'
          }`}
        >
          الكل
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`px-6 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 ${
            filter === 'unread' 
              ? 'bg-white dark:bg-zinc-800 text-slate-900 dark:text-white shadow-sm' 
              : 'text-slate-500 dark:text-zinc-400 hover:text-slate-700 dark:hover:text-zinc-200'
          }`}
        >
          غير مقروءة
          {unreadCount > 0 && (
            <span className="w-5 h-5 bg-primary-500 text-white rounded-full flex items-center justify-center text-[10px] leading-none shrink-0 shadow-lg shadow-primary-500/30">
              {unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {filteredNotifications.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-3xl p-16 text-center shadow-sm"
          >
            <div className="w-24 h-24 bg-slate-50 dark:bg-zinc-800/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <BellRing className="w-12 h-12 text-slate-300 dark:text-zinc-600" />
            </div>
            <h3 className="text-xl font-black text-slate-900 dark:text-white mb-2">لا توجد إشعارات حالياً</h3>
            <p className="text-slate-500 dark:text-zinc-400 font-medium">
              سيتم عرض إشعاراتك الجديدة والتنبيهات المهمة هنا.
            </p>
          </motion.div>
        ) : (
          <AnimatePresence>
            {filteredNotifications.map((notif, index) => (
              <motion.div
                key={notif.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() => handleNotificationClick(notif)}
                className={`group relative overflow-hidden transition-all duration-300 border rounded-2xl cursor-pointer ${
                  !notif.isRead 
                    ? 'bg-white dark:bg-zinc-800/50 border-r-4 border-r-primary-500 border-l-slate-200 border-y-slate-200 dark:border-l-zinc-800 dark:border-y-zinc-800 shadow-md hover:shadow-lg' 
                    : 'bg-slate-50 dark:bg-transparent border-slate-200 dark:border-zinc-800/50 opacity-80 hover:opacity-100 hover:bg-white dark:hover:bg-zinc-900/50'
                }`}
              >
                {!notif.isRead && (
                  <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/10 rounded-full blur-[50px] pointer-events-none group-hover:scale-150 transition-transform duration-700"></div>
                )}
                
                <div className="p-5 md:p-6 flex items-start gap-5 relative z-10">
                  <div className={`p-3 rounded-2xl shrink-0 ${
                    !notif.isRead ? 'bg-slate-100 dark:bg-zinc-800 shadow-inner' : 'bg-transparent'
                  }`}>
                    {getIcon(notif.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0 pt-1">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1.5">
                      <h3 className={`font-bold text-lg md:text-xl truncate pr-2 ${
                        !notif.isRead ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-zinc-300'
                      }`}>
                        {notif.title}
                      </h3>
                      <span className="text-xs font-bold text-slate-400 dark:text-zinc-500 shrink-0 bg-slate-100 dark:bg-zinc-800 px-3 py-1 rounded-full">
                        {notif.createdAt?.toMillis() ? format(notif.createdAt.toMillis(), 'dd MMM yyyy, p', { locale: ar }) : 'الآن'}
                      </span>
                    </div>
                    
                    <p className={`leading-relaxed pr-2 ${
                      !notif.isRead ? 'text-slate-600 dark:text-zinc-300 font-medium' : 'text-slate-500 dark:text-zinc-400'
                    }`}>
                      {notif.message}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
